import React, { useEffect, useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  CheckCircle2,
  Loader2,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Stethoscope,
  Info,
  Link2,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { practitionerService } from '@/services/practitionerService';
import { getDEAValidationError, isValidEmail } from '@/lib/validators';
import { PhoneField } from '@/components/booking/PhoneField';
import PlacesAutocomplete from '@/components/ui/google-places-autocomplete';
import { State } from '@/models/state.model';
import { stateService } from '@/services/stateService';
import { ClinicOnboardingData } from '../types';

// ─── Validation Schema ─────────────────────────────────────────────────────

const ownerBaseSchema = z.object({
  admin_first_name: z.string().min(2, 'First name is required').max(50, 'First name must be at most 50 characters'),
  admin_last_name: z.string().min(2, 'Last name is required').max(50, 'Last name must be at most 50 characters'),
  admin_email: z.string().email('Please enter a valid email address')
    .refine(isValidEmail, { message: 'Email must include a valid domain extension (e.g., .com, .org)' }),
});

const passwordRequiredSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
    confirm_password: z.string().min(1, 'Please confirm your password'),
  })
  .refine(data => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

const passwordOptionalSchema = z.object({
  password: z.string().optional().or(z.literal('')),
  confirm_password: z.string().optional().or(z.literal('')),
});

export type OwnerInfoFormData = z.infer<typeof ownerBaseSchema> & { password?: string; confirm_password?: string };

// ─── Types ──────────────────────────────────────────────────────────────────

export interface OwnerInfoStepHandle {
  triggerValidation: () => Promise<boolean>;
}

interface OwnerInfoStepProps {
  data: ClinicOnboardingData;
  updateData: (updates: Partial<ClinicOnboardingData>) => void;
  onValidationChange?: (isValid: boolean) => void;
  inviteDetails: {
    email: string;
    clinicName: string;
    existingUser?: {
      firstName: string;
      lastName: string;
      hasPassword: boolean;
      isPractitioner?: boolean;
      practitionerId?: string | null;
      practitionerNpi?: string | null;
    } | null;
  } | null;
  requiresPassword: boolean;
  clinicArchetype?: 'guided' | 'direct' | 'hybrid';
}

// ─── Component ──────────────────────────────────────────────────────────────

export const OwnerInfoStep = forwardRef<OwnerInfoStepHandle, OwnerInfoStepProps>(
  ({ data, updateData, onValidationChange, inviteDetails, requiresPassword, clinicArchetype }, ref) => {
    const isExistingPractitionerFromInvite = inviteDetails?.existingUser?.isPractitioner === true;
    const [states, setStates] = useState<State[]>([]);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Practitioner NPI validation state
    const [practitionerNpiValidation, setPractitionerNpiValidation] = useState<{
      isValidating: boolean;
      exists: boolean;
      practitioner?: { id: string; name: string; npi: string; email?: string };
    }>({
      isValidating: Boolean(data.add_as_practitioner && data.existing_practitioner_id),
      exists: false,
    });

    const [addAsPractitioner, setAddAsPractitioner] = useState(data.add_as_practitioner);
    const [useClinicAddress, setUseClinicAddress] = useState(data.use_clinic_address_for_practitioner !== false);
    const [npiTouched, setNpiTouched] = useState(false);

    const isExistingPractitioner = isExistingPractitionerFromInvite
      || (addAsPractitioner && practitionerNpiValidation.exists && !!practitionerNpiValidation.practitioner);

    const credentialOptions = [
      { value: 'MD', label: 'MD - Doctor of Medicine' },
      { value: 'DO', label: 'DO - Doctor of Osteopathic Medicine' },
      { value: 'NP', label: 'NP - Nurse Practitioner' },
      { value: 'PA', label: 'PA - Physician Assistant' },
      { value: 'DVM', label: 'DVM - Doctor of Veterinary Medicine' },
      { value: 'APRN', label: 'APRN - Advanced Practice Registered Nurse' },
      { value: 'CNM', label: 'CNM - Certified Nurse Midwife' },
      { value: 'DNP', label: 'DNP - Doctor of Nursing Practice' },
    ];

    const needsPassword = requiresPassword && !isExistingPractitioner;
    const needsPasswordRef = React.useRef(needsPassword);
    needsPasswordRef.current = needsPassword;

    // Dynamic resolver
    const dynamicResolver = useCallback(
      async (values: any, context: any, options: any) => {
        const schema = ownerBaseSchema.and(
          needsPasswordRef.current ? passwordRequiredSchema : passwordOptionalSchema,
        );
        return zodResolver(schema)(values, context, options);
      },
      [],
    );

    const form = useForm<OwnerInfoFormData>({
      resolver: dynamicResolver,
      defaultValues: {
        admin_first_name: data.admin_first_name || '',
        admin_last_name: data.admin_last_name || '',
        admin_email: data.admin_email || inviteDetails?.email || '',
        password: data.password || '',
        confirm_password: data.confirm_password || '',
      },
      mode: 'onChange',
    });

    // Expose triggerValidation
    useImperativeHandle(ref, () => ({
      triggerValidation: async () => {
        const formValid = await form.trigger();

        // Also validate practitioner NPI if "add as practitioner" is checked
        if (addAsPractitioner && !isExistingPractitioner) {
          const npi = data.practitioner_npi || '';
          if (!npi || npi.length !== 10 || !/^\d{10}$/.test(npi)) {
            setNpiTouched(true);
            // Scroll to the NPI field
            setTimeout(() => {
              const npiError = document.querySelector('[data-npi-error]') ||
                document.querySelector('[placeholder="Enter 10-digit practitioner NPI"]');
              npiError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
            return false;
          }
        }

        return formValid;
      },
    }), [form, addAsPractitioner, isExistingPractitioner, data.practitioner_npi]);

    // Trigger validation on mount when returning with pre-filled data
    useEffect(() => {
      const hasValues = data.admin_first_name && data.admin_last_name;
      if (hasValues) {
        const timer = setTimeout(() => form.trigger(), 150);
        return () => clearTimeout(timer);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch states
    useEffect(() => {
      const fetchStates = async () => {
        try {
          const response = await stateService.getAllStates();
          if (response.data?.states) setStates(response.data.states);
        } catch (error) {
          console.error('Failed to fetch states:', error);
        }
      };
      fetchStates();
    }, []);

    // Pre-fill from invite details
    useEffect(() => {
      if (inviteDetails?.email) {
        form.setValue('admin_email', inviteDetails.email);
      }
      if (inviteDetails?.existingUser) {
        const { firstName, lastName } = inviteDetails.existingUser;
        if (isExistingPractitionerFromInvite) {
          form.setValue('admin_first_name', firstName || '', { shouldValidate: true });
          form.setValue('admin_last_name', lastName || '', { shouldValidate: true });
        } else {
          if (firstName && !form.getValues('admin_first_name')) {
            form.setValue('admin_first_name', firstName, { shouldValidate: true });
          }
          if (lastName && !form.getValues('admin_last_name')) {
            form.setValue('admin_last_name', lastName, { shouldValidate: true });
          }
        }
      }
      if (!requiresPassword) {
        form.setValue('password', '');
        form.setValue('confirm_password', '');
        form.clearErrors(['password', 'confirm_password']);
      }
    }, [inviteDetails, requiresPassword, form, isExistingPractitionerFromInvite]);

    // Auto-fill admin name when existing practitioner found via NPI lookup
    useEffect(() => {
      if (addAsPractitioner && practitionerNpiValidation.exists && practitionerNpiValidation.practitioner) {
        const fullName = practitionerNpiValidation.practitioner.name || '';
        const nameParts = fullName.trim().split(/\s+/);
        form.setValue('admin_first_name', nameParts[0] || '', { shouldValidate: true });
        form.setValue('admin_last_name', nameParts.slice(1).join(' ') || '', { shouldValidate: true });
      }
    }, [addAsPractitioner, practitionerNpiValidation.exists, practitionerNpiValidation.practitioner, form]);

    // Practitioner NPI validation refs
    const lastValidatedPractitionerNpiRef = React.useRef<string>('');
    const practitionerNpiTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const validatePractitionerNpi = useCallback(
      async (npi: string) => {
        if (lastValidatedPractitionerNpiRef.current === npi) return;
        if (!npi || npi.length !== 10 || !/^\d{10}$/.test(npi)) {
          setPractitionerNpiValidation({ isValidating: false, exists: false });
          return;
        }
        lastValidatedPractitionerNpiRef.current = npi;
        setPractitionerNpiValidation(prev => ({ ...prev, isValidating: true }));
        try {
          const result = await practitionerService.checkPractitionerNpiExistsPublic(npi);
          setPractitionerNpiValidation({ isValidating: false, exists: result.exists, practitioner: result.practitioner });
          if (result.exists && result.practitioner) {
            updateData({ existing_practitioner_id: result.practitioner.id });
          } else {
            updateData({ existing_practitioner_id: undefined });
          }
        } catch (error) {
          console.error('Practitioner NPI validation error:', error);
          setPractitionerNpiValidation({ isValidating: false, exists: false });
          updateData({ existing_practitioner_id: undefined });
        }
      },
      [updateData]
    );

    // Re-validate practitioner NPI on mount
    useEffect(() => {
      if (addAsPractitioner && data.practitioner_npi && /^\d{10}$/.test(data.practitioner_npi)) {
        validatePractitionerNpi(data.practitioner_npi);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePractitionerNpiChange = useCallback((npi: string) => {
      updateData({ practitioner_npi: npi });
      if (practitionerNpiTimeoutRef.current) clearTimeout(practitionerNpiTimeoutRef.current);
      if (npi && npi.length === 10 && /^\d{10}$/.test(npi)) {
        if (lastValidatedPractitionerNpiRef.current !== npi) {
          practitionerNpiTimeoutRef.current = setTimeout(() => validatePractitionerNpi(npi), 500);
        }
      } else {
        setPractitionerNpiValidation({ isValidating: false, exists: false });
        lastValidatedPractitionerNpiRef.current = '';
        updateData({ existing_practitioner_id: undefined });
      }
    }, [updateData, validatePractitionerNpi]);

    const handleAddAsPractitionerChange = useCallback((checked: boolean) => {
      setAddAsPractitioner(checked);
      updateData({ add_as_practitioner: checked, use_clinic_address_for_practitioner: checked ? useClinicAddress : undefined });
      if (!checked) {
        setPractitionerNpiValidation({ isValidating: false, exists: false });
        lastValidatedPractitionerNpiRef.current = '';
      } else {
        const currentNpi = data.practitioner_npi;
        if (currentNpi && currentNpi.length === 10 && /^\d{10}$/.test(currentNpi)) {
          lastValidatedPractitionerNpiRef.current = '';
          validatePractitionerNpi(currentNpi);
        }
      }
    }, [updateData, useClinicAddress, data.practitioner_npi, validatePractitionerNpi]);

    const handleUseClinicAddressChange = useCallback((checked: boolean) => {
      setUseClinicAddress(checked);
      updateData({ use_clinic_address_for_practitioner: checked });
    }, [updateData]);

    // Sync form values to parent
    useEffect(() => {
      const subscription = form.watch(values => {
        updateData({
          ...values,
          admin_first_name: values.admin_first_name || '',
          admin_last_name: values.admin_last_name || '',
          admin_email: values.admin_email || inviteDetails?.email || '',
          confirm_password: values.confirm_password || '',
          password: requiresPassword ? values.password || '' : '',
        } as Partial<ClinicOnboardingData>);
      });
      return () => subscription.unsubscribe();
    }, [form, updateData, inviteDetails?.email, requiresPassword]);

    // Re-validate form when existing practitioner state changes
    const isExistingPractitionerPrevRef = React.useRef(isExistingPractitioner);
    useEffect(() => {
      if (isExistingPractitionerPrevRef.current !== isExistingPractitioner) {
        isExistingPractitionerPrevRef.current = isExistingPractitioner;
        if (isExistingPractitioner) {
          form.clearErrors(['password', 'confirm_password']);
          form.setValue('password', '', { shouldValidate: false });
          form.setValue('confirm_password', '', { shouldValidate: false });
        }
      }
    }, [isExistingPractitioner, form]);

    // Update validation status
    useEffect(() => {
      const isValid = form.formState.isValid;
      onValidationChange?.(isValid);
    }, [form.formState.isValid, onValidationChange]);

    // Handle practitioner office address selection
    const handlePractitionerAddressSelect = useCallback(
      (addressData: any) => {
        if (!addressData) return;
        const matchedState = addressData.state
          ? states.find(s => s.code === addressData.state || s.name.toLowerCase() === addressData.state.toLowerCase())
          : undefined;
        updateData({
          practitioner_address_line_1: addressData.address_line_1 || '',
          practitioner_address_line_2: addressData.address_line_2 || '',
          practitioner_city: addressData.city || '',
          practitioner_state: matchedState?.code || '',
          practitioner_postal_code: addressData.postal_code || '',
        });
      },
      [states, updateData]
    );

    return (
      <Form {...form}>
        <form className='space-y-6'>
          {/* Admin Information */}
          {isExistingPractitioner ? (
            <Card className='rounded-xl border border-blue-200 bg-blue-50'>
              <CardContent className='p-5'>
                <div className='flex items-start gap-4'>
                  <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 shadow-md'>
                    <Link2 className='h-5 w-5 text-white' />
                  </div>
                  <div className='space-y-1.5'>
                    <h3 className='text-sm font-semibold text-gray-900'>
                      Existing Practitioner Account Detected
                    </h3>
                    <p className='text-sm text-gray-600'>
                      An existing practitioner account was found
                      {(practitionerNpiValidation.practitioner?.email || inviteDetails?.existingUser?.isPractitioner) && (
                        <> for <strong className='text-gray-900'>
                          {practitionerNpiValidation.practitioner?.email || inviteDetails?.email}
                        </strong></>
                      )}
                      . The clinic admin account will be automatically linked to the practitioner profile.
                    </p>
                    <div className='flex items-center gap-2 pt-1'>
                      <span className='inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-medium text-blue-600'>
                        <Stethoscope className='h-3 w-3' />
                        {practitionerNpiValidation.practitioner?.name
                          || `${inviteDetails?.existingUser?.firstName || ''} ${inviteDetails?.existingUser?.lastName || ''}`.trim()
                          || 'Practitioner'}
                      </span>
                      {(practitionerNpiValidation.practitioner?.npi || inviteDetails?.existingUser?.practitionerNpi) && (
                        <span className='inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-500'>
                          NPI: {practitionerNpiValidation.practitioner?.npi || inviteDetails?.existingUser?.practitionerNpi}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className='rounded-xl border border-gray-100 bg-slate-50 shadow-sm'>
              <CardContent className='space-y-4 p-4'>
                <h3 className='flex items-center gap-2 text-[0.8rem] font-semibold uppercase text-brand-teal tracking-wider'>
                  <CheckCircle2 className='h-4 w-4' />
                  Admin Account Information
                </h3>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                  <FormField
                    control={form.control}
                    name='admin_first_name'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-gray-700 font-medium text-sm'>
                          First Name <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder='Enter your first name' {...field} className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20' />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name='admin_last_name'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className='text-gray-700 font-medium text-sm'>
                          Last Name <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                        </FormLabel>
                        <FormControl>
                          <Input placeholder='Enter your last name' {...field} className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20' />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Email (readonly) */}
                <FormField
                  control={form.control}
                  name='admin_email'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='flex items-center gap-2 text-gray-700 font-medium text-sm'>
                        <Mail className='h-4 w-4' />
                        Email <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type='email'
                          placeholder='Your email address'
                          {...field}
                          readOnly
                          className='cursor-not-allowed bg-gray-50 border-gray-200 text-gray-900 rounded-xl'
                        />
                      </FormControl>
                      <p className='text-xs text-gray-500'>
                        This is the email from your invitation and will be used for login.
                      </p>
                      {!requiresPassword && (
                        <span className='mt-1 inline-flex items-center rounded-full border border-brand-teal/20 bg-brand-teal/10 px-2 py-0.5 text-[11px] font-medium text-brand-teal'>
                          Password already set
                        </span>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {needsPassword ? (
                  <>
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                      <FormField
                        control={form.control}
                        name='password'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className='flex items-center gap-2 text-gray-700 font-medium text-sm'>
                              <Lock className='h-4 w-4' />
                              Password <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                            </FormLabel>
                            <FormControl>
                              <div className='relative'>
                                <Input
                                  type={showPassword ? 'text' : 'password'}
                                  placeholder='Create a password'
                                  {...field}
                                  className='pr-10 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20'
                                />
                                <button
                                  type='button'
                                  onClick={() => setShowPassword(!showPassword)}
                                  className='absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center p-1 rounded text-gray-400 hover:text-gray-600 transition-colors'
                                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                  {showPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='confirm_password'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className='flex items-center gap-2 text-gray-700 font-medium text-sm'>
                              <Lock className='h-4 w-4' />
                              Confirm Password <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                            </FormLabel>
                            <FormControl>
                              <div className='relative'>
                                <Input
                                  type={showConfirmPassword ? 'text' : 'password'}
                                  placeholder='Confirm your password'
                                  {...field}
                                  className='pr-10 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20'
                                />
                                <button
                                  type='button'
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  className='absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center p-1 rounded text-gray-400 hover:text-gray-600 transition-colors'
                                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                >
                                  {showConfirmPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                                </button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <p className='text-xs text-gray-500'>
                      Password must be at least 8 characters and include uppercase, lowercase, number, and special character.
                    </p>
                  </>
                ) : (
                  <div className='rounded-xl border border-brand-teal/20 bg-brand-teal/10 px-3 py-2 text-xs text-brand-teal'>
                    This account already has a password. You will use your existing credentials.
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Add as Practitioner Section — hidden for guided clinics */}
          {clinicArchetype !== 'guided' && (
            <Card className='rounded-xl border border-gray-100 bg-slate-50 shadow-sm'>
              <CardContent className='space-y-4 p-4'>
                <div className='flex items-start space-x-3'>
                  <Checkbox
                    id='add_as_practitioner'
                    checked={addAsPractitioner}
                    onCheckedChange={handleAddAsPractitionerChange}
                    className='mt-0.5'
                  />
                  <div className='flex-1'>
                    <label
                      htmlFor='add_as_practitioner'
                      className='flex items-center gap-2 text-sm font-medium text-gray-900 cursor-pointer'
                    >
                      <Stethoscope className='h-4 w-4 text-brand-teal' />
                      Also register me as a practitioner for this clinic
                    </label>
                    <p className='text-xs text-gray-500 mt-1'>
                      Check this box if you will also be prescribing medications or seeing patients at this clinic.
                    </p>
                  </div>
                </div>

                {/* Conditional Practitioner Fields */}
                {addAsPractitioner && (
                  <div className='mt-4 space-y-4 border-t border-gray-200 pt-4'>
                    <h4 className='flex items-center gap-2 text-[0.75rem] font-semibold uppercase text-gray-500'>
                      <Stethoscope className='h-3.5 w-3.5' />
                      Practitioner Information
                    </h4>

                    {/* NPI Number */}
                    <div className='space-y-1'>
                      <label className='text-sm font-medium text-gray-700'>
                        Practitioner NPI <span className='text-red-500' style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <div className='relative'>
                        <Input
                          placeholder='Enter 10-digit practitioner NPI'
                          maxLength={10}
                          value={data.practitioner_npi || ''}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            handlePractitionerNpiChange(value);
                          }}
                          onBlur={() => { if (!npiTouched) setNpiTouched(true); }}
                          className={`bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20 ${
                            practitionerNpiValidation.exists
                              ? 'border-blue-400'
                              : npiTouched && (!data.practitioner_npi || data.practitioner_npi.length !== 10)
                              ? 'border-red-400'
                              : ''
                          }`}
                        />
                        {practitionerNpiValidation.isValidating && (
                          <div className='absolute right-3 top-1/2 -translate-y-1/2 transform'>
                            <Loader2 className='h-4 w-4 animate-spin text-gray-400' />
                          </div>
                        )}
                        {practitionerNpiValidation.exists && !practitionerNpiValidation.isValidating && (
                          <div className='absolute right-3 top-1/2 -translate-y-1/2 transform'>
                            <Link2 className='h-4 w-4 text-blue-600' />
                          </div>
                        )}
                        {!practitionerNpiValidation.exists && !practitionerNpiValidation.isValidating && data.practitioner_npi?.length === 10 && (
                          <div className='absolute right-3 top-1/2 -translate-y-1/2 transform'>
                            <CheckCircle2 className='h-4 w-4 text-brand-teal' />
                          </div>
                        )}
                      </div>
                      {practitionerNpiValidation.exists && practitionerNpiValidation.practitioner && (
                        <div className='flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded-xl mt-2'>
                          <Info className='h-4 w-4 text-blue-600 mt-0.5 shrink-0' />
                          <div className='text-xs text-blue-600'>
                            <p className='font-medium'>Existing practitioner found</p>
                            <p>
                              <strong>{practitionerNpiValidation.practitioner.name}</strong> (NPI: {practitionerNpiValidation.practitioner.npi})
                              will be linked to your clinic.
                            </p>
                          </div>
                        </div>
                      )}
                      {!practitionerNpiValidation.exists && data.practitioner_npi?.length === 10 && (
                        <p className='text-xs text-brand-teal mt-1'>NPI is available - a new practitioner profile will be created.</p>
                      )}
                      {npiTouched && !practitionerNpiValidation.exists && (!data.practitioner_npi || data.practitioner_npi.length !== 10) && (
                        <p data-npi-error className='text-xs text-red-500 mt-1'>Practitioner NPI must be exactly 10 digits</p>
                      )}
                    </div>

                    {/* Only show additional fields if NPI doesn't exist (new practitioner) */}
                    {!practitionerNpiValidation.exists && data.practitioner_npi?.length === 10 && (
                      <>
                        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                          <div className='space-y-1'>
                            <label className='text-sm font-medium text-gray-700'>Credentials</label>
                            <Select
                              value={data.practitioner_credentials || ''}
                              onValueChange={(value) => updateData({ practitioner_credentials: value })}
                            >
                              <SelectTrigger className='bg-white border-gray-200 text-gray-900'>
                                <SelectValue placeholder='Select credentials' />
                              </SelectTrigger>
                              <SelectContent>
                                {credentialOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className='space-y-1'>
                            <label className='text-sm font-medium text-gray-700'>License Number</label>
                            <Input
                              placeholder='Enter license number'
                              value={data.practitioner_license_number || ''}
                              onChange={(e) => updateData({ practitioner_license_number: e.target.value })}
                              maxLength={30}
                              className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20'
                            />
                          </div>
                        </div>

                        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                          <div className='space-y-1'>
                            <label className='text-sm font-medium text-gray-700'>DEA Number</label>
                            <Input
                              placeholder='Enter DEA number (optional)'
                              value={data.practitioner_dea_number || ''}
                              onChange={(e) => updateData({ practitioner_dea_number: e.target.value.toUpperCase() })}
                              maxLength={20}
                              className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20'
                            />
                            {data.practitioner_dea_number && getDEAValidationError(data.practitioner_dea_number) && (
                              <p className='text-xs text-red-600'>{getDEAValidationError(data.practitioner_dea_number)}</p>
                            )}
                          </div>
                          <PhoneField
                            value={data.practitioner_phone || ''}
                            onChange={(val) => updateData({ practitioner_phone: val })}
                            label="Practitioner Phone"
                            placeholder="Enter phone number"
                            labelClassName="text-sm font-medium text-gray-700"
                            inputClassName="!bg-white !border-gray-200 !text-gray-900 [&::placeholder]:!text-gray-400 [&_.PhoneInputInput]:!bg-white [&_.PhoneInputInput]:!border-gray-200 [&_.PhoneInputInput]:!text-gray-900 [&_.PhoneInputInput]:placeholder:!text-gray-400"
                          />
                          <PhoneField
                            value={data.practitioner_fax || ''}
                            onChange={(val) => updateData({ practitioner_fax: val })}
                            label="Practitioner Fax"
                            placeholder="Enter fax number"
                            labelClassName="text-sm font-medium text-gray-700"
                            inputClassName="!bg-white !border-gray-200 !text-gray-900 [&::placeholder]:!text-gray-400 [&_.PhoneInputInput]:!bg-white [&_.PhoneInputInput]:!border-gray-200 [&_.PhoneInputInput]:!text-gray-900 [&_.PhoneInputInput]:placeholder:!text-gray-400"
                          />
                        </div>

                        {/* Use Clinic Address Checkbox */}
                        <div className='flex items-center space-x-2 pt-2'>
                          <Checkbox
                            id='use_clinic_address'
                            checked={useClinicAddress}
                            onCheckedChange={handleUseClinicAddressChange}
                          />
                          <label htmlFor='use_clinic_address' className='text-sm font-medium text-gray-600 cursor-pointer'>
                            Use clinic address for practitioner
                          </label>
                        </div>

                        {/* Custom Address Fields */}
                        {!useClinicAddress && (
                          <div className='space-y-4 border-t border-gray-200 pt-4'>
                            <h5 className='text-xs font-medium uppercase text-gray-500'>Practitioner Office Address</h5>
                            <div className='relative z-50'>
                              <PlacesAutocomplete
                                onSelectPlace={handlePractitionerAddressSelect}
                                inputClassName='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                                suggestionsClassName='bg-white border-gray-200 [&_li]:text-gray-900 [&_li:hover]:bg-gray-100 [&_li_span.ml-1]:text-gray-500'
                              />
                            </div>
                            <div className='space-y-1'>
                              <label className='text-sm font-medium text-gray-700'>Address Line 1 <span className='text-red-500' style={{ color: '#ef4444' }}>*</span></label>
                              <Input
                                placeholder='Enter street address'
                                value={data.practitioner_address_line_1 || ''}
                                onChange={(e) => updateData({ practitioner_address_line_1: e.target.value })}
                                maxLength={100}
                                className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20'
                              />
                            </div>
                            <div className='space-y-1'>
                              <label className='text-sm font-medium text-gray-700'>Address Line 2</label>
                              <Input
                                placeholder='Suite, floor, etc. (optional)'
                                value={data.practitioner_address_line_2 || ''}
                                onChange={(e) => updateData({ practitioner_address_line_2: e.target.value })}
                                maxLength={100}
                                className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20'
                              />
                            </div>
                            <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                              <div className='space-y-1'>
                                <label className='text-sm font-medium text-gray-700'>City <span className='text-red-500' style={{ color: '#ef4444' }}>*</span></label>
                                <Input
                                  placeholder='Enter city'
                                  value={data.practitioner_city || ''}
                                  onChange={(e) => updateData({ practitioner_city: e.target.value })}
                                  maxLength={50}
                                  className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20'
                                />
                              </div>
                              <div className='space-y-1'>
                                <label className='text-sm font-medium text-gray-700'>State <span className='text-red-500' style={{ color: '#ef4444' }}>*</span></label>
                                <Select
                                  value={data.practitioner_state || ''}
                                  onValueChange={(value) => updateData({ practitioner_state: value })}
                                >
                                  <SelectTrigger className='bg-white border-gray-200 text-gray-900'>
                                    <SelectValue placeholder='Select state' />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {states.map((state) => (
                                      <SelectItem key={state.id} value={state.code}>{state.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className='space-y-1'>
                                <label className='text-sm font-medium text-gray-700'>Postal Code <span className='text-red-500' style={{ color: '#ef4444' }}>*</span></label>
                                <Input
                                  placeholder='Enter postal code'
                                  value={data.practitioner_postal_code || ''}
                                  className='bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-brand-teal focus:ring-brand-teal/20'
                                  onChange={(e) => updateData({ practitioner_postal_code: e.target.value.replace(/\D/g, '') })}
                                  maxLength={5}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </form>
      </Form>
    );
  }
);

OwnerInfoStep.displayName = 'OwnerInfoStep';

export default OwnerInfoStep;
